import React, {useEffect, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import {Button} from "reactstrap";
import Swal from "sweetalert2";
import moment from "moment";
import PhotosPecAPI, {lienPhoto} from "../../../../util/photosPec";
import styles from "./PhotosPriseEnCharge.module.css";

/**
 * Onglet « Photos » d'une prise en charge : trace en images de la réparation (carte mère démontée, état à l'arrivée...).
 * Les photos se prennent avec l'appli iPhone (fiche de la réparation, bouton Photo) ou s'ajoutent ici : bouton,
 * glisser-déposer ou coller (Cmd+V). Un clic ouvre la photo en grand, avec sa légende et la suppression.
 */
const api = new PhotosPecAPI();

function PhotosPriseEnCharge({rootStore, pecId, onNombre}) {
    const [photos, setPhotos] = useState(null);
    const [ouverte, setOuverte] = useState(null);
    const [legende, setLegende] = useState("");
    const [envoi, setEnvoi] = useState(null);
    const [glisse, setGlisse] = useState(false);
    const champFichiers = useRef(null);
    const profondeurGlisse = useRef(0);

    async function charger() {
        try {
            const liste = await api.liste(pecId);
            setPhotos(liste);
            onNombre && onNombre(liste.length);
        } catch (e) {
            setPhotos([]);
        }
    }

    useEffect(() => {
        setPhotos(null);
        setOuverte(null);
        charger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pecId]);

    // Coller une capture ou une photo copiée (Cmd+V) l'ajoute, sauf pendant la saisie d'une légende
    useEffect(() => {
        const coller = e => {
            if (ouverte || ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
            const fichiers = [...(e.clipboardData?.files ?? [])];
            if (fichiers.length > 0) {
                e.preventDefault();
                envoyer(fichiers);
            }
        };
        window.addEventListener("paste", coller);
        return () => window.removeEventListener("paste", coller);
    });

    // Flèches gauche/droite pour passer d'une photo à l'autre, Échap pour fermer
    useEffect(() => {
        if (!ouverte) return;
        const touche = e => {
            if (document.activeElement?.tagName === "INPUT") return;
            if (e.key === "Escape") setOuverte(null);
            if (e.key === "ArrowLeft") voisine(-1);
            if (e.key === "ArrowRight") voisine(1);
        };
        window.addEventListener("keydown", touche);
        return () => window.removeEventListener("keydown", touche);
    });

    function ouvrir(photo) {
        setOuverte(photo);
        setLegende(photo.legende ?? "");
    }

    function voisine(sens) {
        const i = photos.findIndex(p => p.id === ouverte.id);
        const suivante = photos[i + sens];
        if (suivante) ouvrir(suivante);
    }

    async function envoyer(fichiers) {
        const images = [...fichiers].filter(f => f.type.startsWith("image/"));
        if (images.length === 0) {
            rootStore.notificationStore.setNotification("Seules les photos peuvent être ajoutées ici.", "error");
            return;
        }
        let ajoutees = 0;
        for (let i = 0; i < images.length; i++) {
            setEnvoi({numero: i + 1, nombre: images.length, progression: 0});
            try {
                await api.envoyer(pecId, images[i], "", progression => setEnvoi({numero: i + 1, nombre: images.length, progression}));
                ajoutees++;
            } catch (e) {
                // message d'erreur déjà affiché par l'intercepteur d'axios
            }
        }
        setEnvoi(null);
        if (ajoutees > 0) {
            rootStore.notificationStore.setNotification(ajoutees > 1 ? ajoutees + " photos ajoutées" : "Photo ajoutée", "success");
        }
        charger();
    }

    async function enregistrerLegende() {
        if ((ouverte.legende ?? "") === legende.trim()) return;
        try {
            const photo = await api.legende(pecId, ouverte.id, legende);
            setOuverte(photo);
            setPhotos(photos.map(p => p.id === photo.id ? photo : p));
        } catch (e) {
        }
    }

    async function supprimer() {
        const reponse = await Swal.fire({
            title: "Supprimer cette photo ?",
            text: "Elle sera retirée du dossier de la prise en charge.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f86c6b",
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
        });
        if (!reponse.isConfirmed) return;
        try {
            await api.supprimer(pecId, ouverte.id);
            const i = photos.findIndex(p => p.id === ouverte.id);
            const reste = photos.filter(p => p.id !== ouverte.id);
            setPhotos(reste);
            onNombre && onNombre(reste.length);
            const suivante = reste[i] ?? reste[i - 1];
            suivante ? ouvrir(suivante) : setOuverte(null);
        } catch (e) {
        }
    }

    const glisser = {
        onDragEnter: e => {
            e.preventDefault();
            profondeurGlisse.current++;
            setGlisse(true);
        },
        onDragOver: e => e.preventDefault(),
        onDragLeave: () => {
            profondeurGlisse.current--;
            if (profondeurGlisse.current <= 0) setGlisse(false);
        },
        onDrop: e => {
            e.preventDefault();
            profondeurGlisse.current = 0;
            setGlisse(false);
            envoyer(e.dataTransfer.files);
        },
    };

    return (
        <div className={styles.photos} {...glisser}>
            {glisse && <div className={styles.zoneDepot}><i className="fa fa-camera"/> Déposer les photos ici</div>}

            <div className={styles.barre}>
                <Button color="primary" disabled={!!envoi} onClick={() => champFichiers.current.click()}>
                    <i className="fa fa-plus-circle mr-1"/> Ajouter des photos
                </Button>
                <input ref={champFichiers} type="file" accept="image/*" multiple hidden
                       onChange={e => {
                           envoyer(e.target.files);
                           e.target.value = "";
                       }}/>
                {envoi
                    ? <span className={styles.aide}>
                        Envoi {envoi.nombre > 1 ? `${envoi.numero}/${envoi.nombre} ` : ""}… {envoi.progression}%
                      </span>
                    : <span className={styles.aide}>
                        Glisser-déposer ou coller (Cmd+V) des photos ici, ou les prendre avec l'appli iPhone depuis la fiche de la réparation.
                      </span>}
            </div>

            {photos === null && <p className={styles.aide}>Chargement des photos…</p>}
            {photos?.length === 0 && (
                <div className={styles.vide}>
                    <i className="fa fa-camera fa-2x mb-2"/>
                    <div>Aucune photo pour cette prise en charge.</div>
                </div>
            )}

            {photos?.length > 0 && (
                <div className={styles.grille}>
                    {photos.map(photo => (
                        <button key={photo.id} type="button" className={styles.vignette} onClick={() => ouvrir(photo)}
                                title={photo.legende ?? ""}>
                            <img src={lienPhoto(photo.miniature)} alt={photo.legende ?? "Photo"} loading="lazy"/>
                            <span className={styles.legende}>
                                {photo.legende && <strong>{photo.legende}</strong>}
                                <span>{moment(photo.created_at).format("DD/MM/YYYY HH:mm")}{photo.auteur ? " · " + photo.auteur : ""}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {ouverte && (
                <div className={styles.visionneuse} onClick={e => e.target === e.currentTarget && setOuverte(null)}>
                    <div className={styles.cadre}>
                        <a href={lienPhoto(ouverte.url)} target="_blank" rel="noopener noreferrer" title="Ouvrir en taille réelle">
                            <img src={lienPhoto(ouverte.url)} alt={ouverte.legende ?? "Photo"}/>
                        </a>
                        <div className={styles.outils}>
                            <Button color="secondary" outline disabled={photos.findIndex(p => p.id === ouverte.id) === 0}
                                    onClick={() => voisine(-1)} title="Photo précédente (←)"><i className="fa fa-chevron-left"/></Button>
                            <input className="form-control" placeholder="Légende (ex. : carte mère démontée)" value={legende}
                                   maxLength={255}
                                   onChange={e => setLegende(e.target.value)}
                                   onBlur={enregistrerLegende}
                                   onKeyDown={e => e.key === "Enter" && e.target.blur()}/>
                            <Button color="secondary" outline disabled={photos.findIndex(p => p.id === ouverte.id) === photos.length - 1}
                                    onClick={() => voisine(1)} title="Photo suivante (→)"><i className="fa fa-chevron-right"/></Button>
                            <Button color="danger" outline onClick={supprimer} title="Supprimer la photo"><i className="fa fa-trash"/></Button>
                            <Button color="secondary" onClick={() => setOuverte(null)}>Fermer</Button>
                        </div>
                        <div className={styles.aide}>
                            {moment(ouverte.created_at).format("DD/MM/YYYY à HH:mm")}{ouverte.auteur ? ", par " + ouverte.auteur : ""}
                            {" · "}{ouverte.largeur} × {ouverte.hauteur}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default inject("rootStore")(observer(PhotosPriseEnCharge));
